import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer, type JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import type { AIPlugin } from '../aiPluginType';
import type { EndSessionMetadata } from '../editor-actions/types';
import { aiSessionContextPluginKey } from '../pm-plugins/ai-session-context/ai-session-context-plugin-key';
import {
	AI_CONTENT_SEGMENT_DATA,
	type AIContentSegmentData,
	type AIContentSegmentEntry,
	AI_ORIGINAL_NODE_DATA,
	type AIOriginalNodeData,
} from '../pm-plugins/ai-session-context/meta-keys';
import type {
	PostStreamReviewAcceptedUpdate,
	PostStreamReviewAppliedIndexUpdate,
	PostStreamReviewPositionsUpdate,
	StreamReviewMode,
} from '../pm-plugins/ai-session-context/types';
import {
	createEndAIExperienceCommand,
	createCommitInlineRovoStreamingCommand,
	createEndInlineRovoStreamingCommand,
	createOpenAIModalCommand,
	createStageInlineRovoStreamingCommand,
	createStartAISessionCommand,
	createUpdateTargetIdCommand,
} from '../pm-plugins/decoration/actions';
import { createCommand, getPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import type { AgentInfo } from '../pm-plugins/decoration/reducer';
import { endStream } from '../pm-plugins/pure-pm-in-editor-direct-streaming/transforms';
import type { AIGlobalOptIn } from '../types/types';
import type { EditorPluginAIConfigItem } from '../ui/prebuilt/config-items/config-items';
import type { InvokedFrom } from '../ui/utils/analytics/analyticsFlowTypes';
import { isNewAifcExperienceEnabled } from '../ui/utils/new-aifc-experience-enabled';

export const openAIModal = ({
	invokedFrom,
	aiOptIn,
	aiSessionContextType,
	configItem,
	editorView,
	triggeredFor,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
	traceId,
	singleInstrumentationId,
	isCreate,
	promptVersion,
	userADFInput,
	overrideFromPos,
	overrideToPos,
	shouldExpandPositions,
	beforeTransform,
	skipSelectionHighlight,
}: {
	aiOptIn: AIGlobalOptIn;
	aiSessionContextType?:
		| { type: 'PROMPT' }
		| { type: 'STREAM' }
		| {
				/**
				 * Optional acceptance flip forwarded into the reducer meta so marking a
				 * segment accepted and re-anchoring the modal land in one transaction.
				 * See `PostStreamReviewAction.acceptedUpdate`.
				 */
				acceptedUpdate?: PostStreamReviewAcceptedUpdate;
				aiOptIn?: AIGlobalOptIn;
				/**
				 * Optional applied-index update applied atomically as part of the
				 * `POST_STREAM_REVIEW` open. Forwarded verbatim into the reducer meta so
				 * the slice-replace and the state change land in the same transaction. See
				 * `PostStreamReviewAction.appliedIndexUpdate`.
				 */
				appliedIndexUpdate?: PostStreamReviewAppliedIndexUpdate;
				currentStreamReviewSegmentIndex: number;
				mode?: StreamReviewMode;
				/**
				 * Optional authoritative position writeback forwarded into the reducer
				 * meta. See `PostStreamReviewAction.positionsUpdate`.
				 */
				positionsUpdate?: PostStreamReviewPositionsUpdate;
				type: 'POST_STREAM_REVIEW';
		  };
	/**
	 * Forwarded verbatim to `createOpenAIModalCommand`. Allows the
	 * caller to piggy-back domain-specific edits onto the same transaction that
	 * opens the modal (avoiding a separate dispatch). See
	 * `createOpenAIModalCommand` for the full contract.
	 */
	beforeTransform?: (tr: Transaction) => Transaction;
	configItem: EditorPluginAIConfigItem;
	editorView?: EditorView;
	hideLoadingScreen?: boolean;
	invokedFrom: InvokedFrom;
	isCreate?: boolean;
	isExternal?: boolean;
	/**
	 * Optional override for the modal-anchor start position. When
	 * omitted the modal is anchored at the current selection. Used by the
	 * Post Stream Review modal to anchor at a specific AI change segment.
	 */
	overrideFromPos?: number;
	/** Companion to `overrideFromPos`. */
	overrideToPos?: number;
	promptVersion?: string;
	/** Forwarded to `createOpenAIModalCommand`. */
	shouldExpandPositions?: boolean;
	singleInstrumentationId?: string;
	/**
	 * When true, the modal is still decorated and anchored but the range
	 * selection highlight is omitted. Used by the Post Stream Review modal.
	 */
	skipSelectionHighlight?: boolean;
	traceId?: string;
	triggeredFor?: NodeType;
	userADFInput?: JSONDocNode;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
}): void => {
	if (!editorView) {
		return;
	}

	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		lastTriggeredFrom: invokedFrom,
		aiGlobalOptIn: aiOptIn,
		aiSessionContextType,
		triggeredFor,
		isExternal,
		withAcceptReject,
		withStreamingHighlight,
		hideLoadingScreen,
		traceId,
		singleInstrumentationId,
		isCreate,
		promptVersion,
		userADFInput,
		overrideFromPos,
		overrideToPos,
		shouldExpandPositions,
		beforeTransform,
		skipSelectionHighlight,
	});

	openAIModalCommand(editorView.state, editorView.dispatch);
};

export const endAIExperience = ({ editorView }: { editorView?: EditorView }): void => {
	if (!editorView) {
		return;
	}

	const endAIExperienceCommand = createEndAIExperienceCommand();
	endAIExperienceCommand(editorView.state, editorView.dispatch);
};

export const startAISession = ({
	editorView,
	sessionId,
	traceId,
	promptVersion,
	agentInfo,
	isCreateSession,
}: {
	agentInfo?: AgentInfo;
	editorView?: EditorView;
	/** True for a create stream; suppresses Post Stream Review. */
	isCreateSession?: boolean;
	promptVersion?: string;
	sessionId: string;
	traceId?: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	return createStartAISessionCommand(
		sessionId,
		traceId,
		fg('platform_editor_ai_promptversion_in_analytics') ? promptVersion : undefined,
		agentInfo,
		isCreateSession,
	)(editorView.state, editorView.dispatch);
};

export const endAISession = ({
	api,
	editorView,
	sessionId,
	metadata,
	baseGenerate,
	aiOptIn,
}: {
	/**
	 * Forwarded to `openPostStreamReviewModal` → `openAIModal` for the
	 * auto-open after a clean AI session end. Optional; when omitted the auto-open
	 * is skipped.
	 */
	aiOptIn?: AIGlobalOptIn;
	/**
	 * The Post Stream Review modal — auto-opened at the end of a
	 * successful AI session when the new AIFC experience gate is ON — needs a
	 * `configItem` to dispatch `SHOW_MODAL` with. We always use
	 * `editorPluginAIProvider.baseGenerate` because it is the canonical
	 * "AI session" config item that every product registers and that is wired
	 * up to the same prompt / streaming code paths as the Post Stream Review
	 * "Ask Rovo" prompt input.
	 *
	 * Optional so this signature remains backwards-compatible with non-xstate /
	 * non-new-AIFC callers; when omitted the auto-open is skipped.
	 */
	api?: ExtractInjectionAPI<AIPlugin>;
	baseGenerate?: EditorPluginAIConfigItem;
	editorView?: EditorView;
	metadata: EndSessionMetadata;
	sessionId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	// Read before the END_SESSION dispatch clears it: a create stream has nothing
	// to review, so the Post Stream Review auto-open below is suppressed.
	const isCreateSession = !!aiSessionContextPluginKey.getState(editorView.state)?.isCreateSession;

	// [platform_editor_remove_pause_streaming] When aborting during streaming
	// (e.g. from the Rovo sidebar stop button), dispatch COMPLETE to commit
	// partial content, and endStream to re-enable the editor.
	// For the inline stop path, Loading.tsx dispatches
	// these BEFORE calling abort(), so status is already 'complete' here and
	// this block is skipped.
	if (
		metadata?.type === 'abort' &&
		expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true) &&
		api
	) {
		commitPartialStreamAndCleanup({ api, editorView });
	}

	const endSessionCommand = createCommand(
		(state) => {
			const aiDecorationExperiencePluginState = getPluginState(state);
			const nextSessionId = aiDecorationExperiencePluginState.nextSessionId;

			// If a sessions which is not the next session is trying to be ended then we should abort trying to end the session.
			if (sessionId !== nextSessionId) {
				return false;
			}

			return {
				type: 'END_SESSION',
				data: { sessionId: undefined, type: metadata?.type },
			};
		},
		(tr) => {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				// When the New AIFC Editor Experience is enabled, fire END_SESSION
				// instead of RESET so the `aiContentPositions` array is retained for the
				// Post Stream Review toolbar to navigate.
				tr.setMeta(aiSessionContextPluginKey, {
					type: isNewAifcExperienceEnabled() ? 'END_SESSION' : 'RESET',
				});
			}

			return tr;
		},
	);

	const result = endSessionCommand(editorView.state, editorView.dispatch);

	// After ending the session, if positions were captured and the
	// session ended cleanly ('finished'), automatically open the Post Stream Review
	// modal in the next tick (so the END_SESSION transaction has been applied first).
	if (
		result &&
		// Never auto-open the Review moment for a create stream.
		!isCreateSession &&
		isNewAifcExperienceEnabled() &&
		(metadata?.type === 'finished' ||
			(metadata?.type === 'abort' &&
				expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true))) &&
		baseGenerate &&
		aiOptIn
	) {
		// Defer so the END_SESSION transaction is committed before reading state.
		Promise.resolve().then(() => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}

			const sessionState = aiSessionContextPluginKey.getState(editorView.state);
			const reviewSegments = sessionState?.streamReviewSegments ?? [];
			if (reviewSegments.length) {
				const displaySegments = reviewSegments.flatMap((segment) =>
					segment.diffSegments?.length ? segment.diffSegments : [segment],
				);
				const desiredIndex = sessionState?.currentStreamReviewSegmentIndex ?? 0;
				const segmentIndex =
					displaySegments.length > 0
						? Math.max(0, Math.min(desiredIndex, displaySegments.length - 1))
						: 0;
				const segment = displaySegments[segmentIndex];
				if (segment) {
					openPostStreamReviewModal({
						editorView,
						baseGenerate,
						aiOptIn,
						segmentData: {
							segmentIndex,
							positions: { startPos: segment.startPos, endPos: segment.endPos },
						},
					});
					return;
				}
			}

			const positions = sessionState?.aiContentPositions ?? [];
			if (positions.length) {
				// The Post Stream Review modal opens in AGGREGATE mode by
				// default. In aggregate mode it is:
				//   - anchored below the LAST segment (the most recent change), so the
				//     user lands at the end of the AI's edits, and
				//   - highlighted across ALL segments — we span the highlight from the
				//     earliest segment start to the latest segment end so the whole
				//     reviewed region is visible at once.
				const lastIndex = positions.length - 1;
				const aggregateStart = Math.min(...positions.map((p) => p.startPos));
				const aggregateEnd = Math.max(...positions.map((p) => p.endPos));
				openPostStreamReviewModal({
					editorView,
					baseGenerate,
					aiOptIn,
					mode: 'aggregate',
					segmentData: {
						segmentIndex: lastIndex,
						positions: { startPos: aggregateStart, endPos: aggregateEnd },
					},
				});
			}
		});
	}

	return result;
};

/**
 * Describes which AI change segment the Post Stream Review modal
 * should anchor at.
 *
 * `segmentIndex` is always required (it's stamped onto
 * `aiSessionContextPluginKey` so the `POST_STREAM_REVIEW` reducer updates
 * `currentStreamReviewSegmentIndex`).
 *
 * `positions` is optional:
 *   - **Omitted (default flow):** positions are read from
 *     `sessionState.aiContentPositions[segmentIndex]`. This is the normal path
 *     for Previous / Next navigation and the initial auto-open after streaming
 *     finishes — by then `buildCommitTransaction` has populated
 *     `aiContentPositions`.
 *   - **Provided:** caller knows exactly where the modal should anchor (e.g.
 *     Undo / Redo: the slice-replace `beforeTransform` has already mutated the
 *     doc and computed the new `[startPos, endPos]`, but the session-context
 *     plugin's `apply` hasn't committed `UPDATE_AI_CONTENT_POSITION` yet — at
 *     the point `openPostStreamReviewModal` runs, `aiContentPositions` is
 *     still the pre-replace value. Passing positions here lets the modal
 *     anchor at the post-replace range directly).
 */
export type PostStreamReviewSegmentData = {
	positions?: { endPos: number; startPos: number };
	segmentIndex: number;
};

export const openPostStreamReviewModal = ({
	editorView,
	aiOptIn,
	baseGenerate,
	segmentData = { segmentIndex: 0 },
	beforeTransform,
	appliedIndexUpdate,
	acceptedUpdate,
	positionsUpdate,
	mode,
}: {
	/**
	 * Optional acceptance flip to apply atomically with the modal open. Threaded
	 * into the `POST_STREAM_REVIEW` meta the same way as `appliedIndexUpdate`.
	 */
	acceptedUpdate?: PostStreamReviewAcceptedUpdate;
	/**
	 * The global opt-in state. Required by `openAIModal` / `createOpenAIModalCommand`
	 * to gate the modal behind FedRAMP / opt-in checks.
	 *
	 * Optional — when omitted (e.g. Previous / Next re-opens from inside the screen),
	 * we fall back to `currentStreamReviewAiOptIn` stored on the session-context
	 * plugin state from the original open.
	 */
	aiOptIn?: AIGlobalOptIn;
	/**
	 * Optional Undo/Redo applied-index update to apply atomically with the modal
	 * open. Threaded into the `POST_STREAM_REVIEW` meta so the slice-replace (via
	 * `beforeTransform`), the modal re-open, and the `appliedIndex` change all
	 * commit in a single transaction — avoiding a sub-frame window where
	 * `canUndo`/`canRedo` briefly show the wrong state.
	 */
	appliedIndexUpdate?: PostStreamReviewAppliedIndexUpdate;
	/**
	 * Optional override for the config item used to dispatch `SHOW_MODAL`.
	 *
	 * - When called from `endAISession` (auto-open after a successful AI session) the
	 *   caller threads `editorPluginAIProvider.baseGenerate` down explicitly.
	 * - When called from inside the Post Stream Review screen itself (Previous/Next
	 *   re-open at a different segment), the modal is already showing with the
	 *   `baseGenerate` config item set on the decoration plugin state, so we read it
	 *   back from there as a fallback.
	 */
	baseGenerate?: EditorPluginAIConfigItem;
	/**
	 * Forwarded verbatim to `openAIModal` → `createOpenAIModalCommand`
	 * so the caller can piggy-back domain-specific doc edits (e.g. the
	 * Undo/Redo slice-replace inside the Post Stream Review screen) onto the
	 * same transaction that re-issues `SHOW_MODAL`. Avoids the two-dispatch
	 * pattern (dispatch slice-replace, then in a microtask dispatch
	 * `openPostStreamReviewModal`) which could briefly leave the modal
	 * anchored at a stale or collapsed decoration range.
	 */
	beforeTransform?: (tr: Transaction) => Transaction;
	editorView?: EditorView;
	/**
	 * Optional review mode override for this modal open.
	 */
	mode?: StreamReviewMode;
	/**
	 * Optional authoritative position writeback applied atomically with the modal
	 * open. See `PostStreamReviewAction.positionsUpdate`.
	 */
	positionsUpdate?: PostStreamReviewPositionsUpdate;
	/**
	 * Which segment to anchor the modal at, and (optionally) the
	 * positions to use directly instead of reading from
	 * `aiContentPositions[segmentIndex]`. See `PostStreamReviewSegmentData`
	 * for the full contract.
	 */
	segmentData?: PostStreamReviewSegmentData;
}): void => {
	if (!editorView) {
		return;
	}

	const { segmentIndex, positions: explicitPositions } = segmentData;

	const sessionState = aiSessionContextPluginKey.getState(editorView.state);
	// Prefer caller-supplied positions when provided (Undo/Redo case).
	// Otherwise fall back to the positions stored on the session-context plugin —
	// the normal path for the initial auto-open and Previous/Next navigation.
	const segment = explicitPositions ?? sessionState?.aiContentPositions?.[segmentIndex];
	if (!segment) {
		return;
	}

	const configItem = baseGenerate ?? getPluginState(editorView.state).configItem;
	if (!configItem) {
		return;
	}

	const resolvedAiOptIn = aiOptIn ?? sessionState?.currentStreamReviewAiOptIn;
	if (!resolvedAiOptIn) {
		return;
	}

	openAIModal({
		editorView,
		aiOptIn: resolvedAiOptIn,
		configItem,
		// (afm-ai-pr-review-bot #3) `'postStreamReview'` is now a first-class
		// member of the `InvokedFrom` union (see analyticsFlowTypes.ts), so the
		// `as InvokedFrom` cast that previously bypassed the type system is no
		// longer needed.
		invokedFrom: 'postStreamReview',
		// Anchor the modal at the segment's range — not the user's current selection.
		overrideFromPos: segment.startPos,
		overrideToPos: segment.endPos,
		shouldExpandPositions: false,
		// The Post Stream Review screen handles its own accept / reject (undo / redo
		// against stored slices), and there is no streaming to highlight.
		withAcceptReject: false,
		withStreamingHighlight: false,
		hideLoadingScreen: true,
		aiSessionContextType: {
			type: 'POST_STREAM_REVIEW',
			currentStreamReviewSegmentIndex: segmentIndex,
			aiOptIn: resolvedAiOptIn,
			appliedIndexUpdate,
			acceptedUpdate,
			positionsUpdate,
			mode,
		},
		isExternal: true,
		beforeTransform,
		// Keep the modal anchored via mount-point decorations, but don't
		// highlight the reviewed text in the Review moment.
		skipSelectionHighlight: true,
	});
};

export const updateTargetId = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string | number;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createUpdateTargetIdCommand(targetId)(editorView.state, editorView.dispatch);
};

export const stageInlineRovoStreaming = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createStageInlineRovoStreamingCommand(targetId)(editorView.state, editorView.dispatch);
};

export const commitInlineRovoStreaming = ({
	editorView,
}: {
	editorView?: EditorView;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createCommitInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
};

export const endInlineRovoStreaming = ({
	editorView,
}: {
	editorView?: EditorView;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
};

interface GetPartialPMNodeFromSelectionOptions {
	maxSize?: number;
	selection: Selection | undefined;
}

export const getPartialPMNodeFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => PMNode | undefined = ({ selection, maxSize = 10000 }) => {
	if (!selection) {
		return undefined;
	}

	const { $from, $to } = selection;

	const doc = $from.doc;

	/**
	 * Check the from and to positions of the lowest depth level (doc level).
	 * If the size is over the threshold, increase the depth.
	 */

	let depth = 0;

	while (
		// Check until max depth is reached from the resolved positions
		!isNaN($from.start(depth)) &&
		!isNaN($to.end(depth))
	) {
		const from = $from.start(depth);
		const to = $to.end(depth);
		if (to - from < maxSize) {
			break;
		}
		depth++;
	}

	if (depth === 0) {
		return doc;
	}

	// find a depth that fits and then start looking at siblings n - 1 and n + 1 and so on

	const parentDepth = depth - 1;
	const index = $from.index(parentDepth);

	let from = $from.start(depth);
	let to = $to.end(depth);
	let increment = 1;

	const maxIndex = $from.node(parentDepth).childCount;

	while (
		// Loop until both ends of the bounds have been reached
		index - increment >= 0 ||
		index + increment <= maxIndex
	) {
		const newFrom = $from.posAtIndex(Math.max(index - increment, 0), parentDepth);
		// We look ahead at the next index as posAtIndex yields the starting position of the node
		const newTo = $to.posAtIndex(Math.min(index + increment + 1, maxIndex), parentDepth);

		let isPositionUpdated = false;
		if (to - newFrom < maxSize) {
			from = newFrom;
			isPositionUpdated = true;
		}
		if (newTo - from < maxSize) {
			to = newTo;
			isPositionUpdated = true;
		}

		if (!isPositionUpdated) {
			break;
		}
		increment++;
	}

	return doc.cut(from, to);
};

export const getPartialDocumentFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => DocNode | undefined = (options) => {
	const transformer = new JSONTransformer();
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return transformer.encode(doc) as DocNode;
	}
};

export const getPartialMarkdownFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => string | undefined = (options) => {
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return convertProsemirrorNodeToMarkdown({ node: doc })?.markdown ?? '';
	}
};

export const tagAsAIStreamingTransformation = (
	tr: Transaction,
	options?: {
		isFinal: boolean;
	},
): Transaction => {
	if (options?.isFinal) {
		return tr.setMeta('isAIFinalStreamingTransformation', true);
	}
	return tr.setMeta('isAIStreamingTransformation', true);
};

/**
 * Record one or more coarse AI content position entries for a
 * NON-STREAMABLE command (removeNode, setAttrs, move, wrap, ...).
 *
 * These commands mutate the document directly in their handler and never reach
 * `buildCommitTransaction`, so they would otherwise leave no reviewable segment
 * for the Post Stream Review modal. Stamping the AI_CONTENT_SEGMENT_DATA meta
 * lets `aiSessionContextPlugin.apply()` append these entries to
 * `aiContentPositions`, exactly like a streamable commit.
 *
 * Multiple calls on the same transaction accumulate (entries are merged).
 *
 * @param tr The transaction the handler is building (after the mutation).
 * @param segments The coarse entries to record.
 */
export const recordAIContentSegments = (
	tr: Transaction,
	segments: AIContentSegmentEntry[],
): Transaction => {
	// Post Stream Review (and therefore segment recording) only exists under the
	// New AIFC Editor Experience. When the gate is OFF there is no PSR, so this
	// is a no-op — handlers can call it unconditionally.
	if (!isNewAifcExperienceEnabled()) {
		return tr;
	}
	if (!segments || segments.length === 0) {
		return tr;
	}
	const existing = tr.getMeta(AI_CONTENT_SEGMENT_DATA) as AIContentSegmentData | undefined;
	const merged: AIContentSegmentData = {
		segments: [...(existing?.segments ?? []), ...segments],
	};
	return tr.setMeta(AI_CONTENT_SEGMENT_DATA, merged);
};

/**
 * Record the exact node a `replaceNode` command located, so
 * `buildMountTransaction` can rebuild an accurate single-node
 * `originalUserContentSlice` instead of relying on the doc-range slice.
 *
 * Stamps `AI_ORIGINAL_NODE_DATA`, which `apply()` stores on the
 * `replaceNodeOriginalNode` session-state field. Does not create a reviewable
 * segment. No-op when the new AIFC experience gate is off.
 */
export const recordOriginalNode = (tr: Transaction, originalNode: PMNode): Transaction => {
	if (!isNewAifcExperienceEnabled()) {
		return tr;
	}
	const data: AIOriginalNodeData = { originalNode };
	return tr.setMeta(AI_ORIGINAL_NODE_DATA, data);
};

/**
 * [platform_editor_remove_pause_streaming]
 * Commits partial AI-streamed content and cleans up streaming state.
 *
 * 1. COMPLETE — triggers handleFinalize (restore → commit), keeping partial
 *    content in the document and populating aiContentPositions for PSR.
 * 2. endStream — resets the streaming plugin (isStreaming/isStreamingFinished)
 *    so the editor is re-enabled (Update button becomes clickable).
 */
export function commitPartialStreamAndCleanup({
	api,
	editorView,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorView: EditorView;
}): void {
	const sessionState = aiSessionContextPluginKey.getState(editorView.state);
	if (!sessionState?.adfResponse || sessionState.status !== 'streaming') {
		return;
	}

	api.aiExperience.actions.dispatchAIEvent({
		type: 'complete',
		adfResponse: sessionState.adfResponse,
		markdown: sessionState.markdown,
	});

	const tr = editorView.state.tr;
	endStream(tr);
	editorView.dispatch(tr);
}
